import type { IframelyMedia } from '@prismamedia/one-components';
import { sleep } from '../sleep';

/**
 * Product rule (image file upload only):
 * on every Iframely call **for an uploaded image**, the response must include `meta` and
 * `meta.site === PMD_BONE_Image`. Enforced by `fetchBoneImageIframelyWithRetry`
 * (see `useUploadImage` / `uploadAndEmbedImageUrl`).
 *
 * Outside that flow, an article may embed other types (`PMD_BONE_Article`, `PMD_Slideshow`, …)
 * aligned with the API `KnownIframelySite` — save-time validation only targets Bone pipeline
 * images (see `isBonePipelineImageIframely` / `validateArticle`).
 */
export const PMD_BONE_IMAGE_SITE = 'PMD_BONE_Image' as const;

/** Known sites (see API `KnownIframelySite`): no `PMD_BONE_Image` requirement on save. */
const IFRAMELY_SITES_EXEMPT_FROM_BONE_IMAGE_META = new Set<string>([
  'PMD_BONE_Article',
  'PMD_Slideshow',
  'Dailymotion',
  'youtube',
  'Youtube',
  'Brightcove',
  'plurimedia',
  'Prisma Media Player',
]);

/** User-facing message when image upload + Iframely fails after retries. */
export const IFRAMELY_IMAGE_UPLOAD_ERROR_MESSAGE =
  "L'image n'a pas pu être traitée correctement. Veuillez réessayer l'upload.";

/** Expected Iframely success **after an image upload** only (`meta` + Bone image `site`). */
const isValidBoneImageIframelyMeta = (
  iframely: IframelyMedia | undefined | null,
): boolean =>
  Boolean(iframely?.meta && iframely.meta.site === PMD_BONE_IMAGE_SITE);

/** Persisted Bone image (API payload): `site` may be missing for a valid stored image. */
const hasBoneAssetStorageMeta = (
  meta: IframelyMedia['meta'] | undefined,
): boolean =>
  Boolean(
    meta &&
      typeof (meta as { path?: unknown }).path === 'string' &&
      typeof (meta as { namespace?: unknown }).namespace === 'string',
  );

/**
 * Media produced by the Bone image pipeline (upload / `createImageFromUrl`) or persisted
 * Bone asset storage. Other embeds (Instagram, widgets, internal article cards, …) are false.
 */
export const isBonePipelineImageIframely = (
  iframely: IframelyMedia | undefined | null,
): boolean => {
  const meta = iframely?.meta;
  if (!meta) {
    return false;
  }
  if (meta.site === PMD_BONE_IMAGE_SITE) {
    return true;
  }
  return hasBoneAssetStorageMeta(meta);
};

/**
 * Save-time check for an **article** (not the same rule as upload-time Iframely).
 * Allows article-like, slideshow-like, video-like embeds, etc., or a Bone image
 * (`PMD_BONE_Image` or storage meta `path` + `namespace`).
 */
export const isArticleIframelyMetaAcceptable = (
  iframely: IframelyMedia | undefined | null,
): boolean => {
  if (!iframely?.meta) {
    return false;
  }
  const site = iframely.meta.site as string | undefined;
  if (site && IFRAMELY_SITES_EXEMPT_FROM_BONE_IMAGE_META.has(site)) {
    return true;
  }
  if (isValidBoneImageIframelyMeta(iframely)) {
    return true;
  }
  if (hasBoneAssetStorageMeta(iframely.meta)) {
    return true;
  }
  return false;
};

/**
 * **Slideshow slide** check: only a Bone image (upload / storage),
 * not an article or slideshow embed in another shape.
 */
export const isSlideshowSlideIframelyMetaAcceptable = (
  iframely: IframelyMedia | undefined | null,
): boolean =>
  isValidBoneImageIframelyMeta(iframely) ||
  hasBoneAssetStorageMeta(iframely?.meta);

const RETRY_DELAYS_MS = [800, 1200];

/** Iframely calls **only for the image-upload flow**: `meta` + `PMD_BONE_Image`, with retries. */
export const fetchBoneImageIframelyWithRetry = async (
  fetchOnce: () => Promise<IframelyMedia>,
): Promise<IframelyMedia> => {
  const attempt = async (attemptIndex: number): Promise<IframelyMedia> => {
    const data = await fetchOnce();
    if (isValidBoneImageIframelyMeta(data)) {
      return data;
    }
    if (attemptIndex >= RETRY_DELAYS_MS.length) {
      throw new Error(IFRAMELY_IMAGE_UPLOAD_ERROR_MESSAGE);
    }
    const delayMs = RETRY_DELAYS_MS[attemptIndex];
    if (delayMs === undefined) {
      throw new Error(IFRAMELY_IMAGE_UPLOAD_ERROR_MESSAGE);
    }
    await sleep(delayMs);
    return attempt(attemptIndex + 1);
  };
  return attempt(0);
};
